import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { marked } from 'marked'
import './help.css'

interface TocItem {
  id: string
  text: string
  level: number
}

export const Help: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [raw, setRaw] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeId, setActiveId] = useState('')

  const lang = ['en', 'es'].includes(i18n.language) ? i18n.language : 'en'

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetch(`/help/${lang}.md`)
      .then((res) => {
        if (!res.ok) throw new Error(t('help.loadError') || 'Failed to load help')
        return res.text()
      })
      .then((text) => {
        setRaw(text)
        setLoading(false)
      })
      .catch((err: Error) => {
        setError(err.message)
        setLoading(false)
      })
  }, [lang, t])

  const { html, toc } = useMemo(() => {
    if (!raw) return { html: '', toc: [] as TocItem[] }

    const tocItems: TocItem[] = []
    const renderer = new marked.Renderer()

    renderer.heading = ({ text, depth }: { text: string; depth: number }) => {
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '')
      if (depth >= 2) {
        tocItems.push({ id, text, level: depth })
      }
      return `<h${depth} id="${id}">${text}</h${depth}>`
    }

    renderer.link = ({ href, text }: { href: string; text: string }) => {
      const isExternal = href.startsWith('http')
      return `<a href="${href}" ${isExternal ? 'target="_blank" rel="noopener noreferrer"' : ''}>${text}</a>`
    }

    const parsed = marked.parse(raw, { renderer })
    const htmlString = typeof parsed === 'string' ? parsed : ''
    return { html: htmlString, toc: tocItems }
  }, [raw])

  useEffect(() => {
    if (!html) return
    const headings = Array.from(document.querySelectorAll('.r3c-help-content [id]'))
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id)
        })
      },
      { rootMargin: '-20% 0px -60% 0px' }
    )

    headings.forEach((h) => observer.observe(h))
    return () => observer.disconnect()
  }, [html])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
      setActiveId(id)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-400">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-8rem)]">
      {/* Table of Contents */}
      <aside className="lg:w-72 flex-shrink-0 bg-[#12121a] border border-white/10 rounded-xl p-4 overflow-hidden flex flex-col">
        <h2 className="text-lg font-semibold text-white mb-4">
          {t('help.title') || 'User Manual'}
        </h2>
        <nav className="flex-1 overflow-y-auto pr-1">
          <ul className="space-y-1">
            {toc.map((item) => (
              <li
                key={item.id}
                style={{ paddingLeft: `${(item.level - 2) * 0.75}rem` }}
              >
                <button
                  onClick={() => scrollTo(item.id)}
                  className={`text-left text-sm w-full transition-colors ${
                    activeId === item.id
                      ? 'text-[#006cd4] font-medium'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {item.text}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-[#12121a] border border-white/10 rounded-xl p-6 lg:p-10">
        <div
          className="r3c-help-content max-w-4xl mx-auto"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <div className="max-w-4xl mx-auto mt-10 pt-6 border-t border-white/10">
          <p className="text-sm text-gray-400">
            {t('help.needMoreHelp')}{' '}
            <Link to="/support" className="text-[#006cd4] hover:underline">
              {t('help.contactSupport')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
